/**
 * This module provides a React Context for managing the current account ID throughout the application.
 * It includes a provider component and a custom hook for accessing the account context.
 */

import { createContext, useContext } from 'react';

/**
 * Interface defining the shape of the account context value.
 */
interface AccountContextType {
  /** The current account ID, or null if no account is selected */
  accountId: string | null;
  /** Function to update the current account ID */
  setAccountId: (id: string | null) => void;
}

/**
 * React Context instance for the account management.
 * Initialized with default values that will be overridden by the Provider.
 */
export const AccountContext = createContext<AccountContextType>({
  accountId: null,
  setAccountId: () => {}
});

/**
 * Custom hook to access the account context from any component.
 * @returns The current account context value
 * @throws {Error} If used outside of an AccountProvider
 */
export const useAccountId = () => useContext(AccountContext);
